/* ↗ .cursor/references/code-notes/r-utility-drawers.js.md#§1 */

import { onAnimationEnd } from '@theme/utilities';

const AUTO_OPEN_SELECTOR = '[data-r-drawer-autoopen]';
const REMOTE_TRIGGER_SELECTOR = '[data-r-drawer-open]';

/* ↗ .cursor/references/code-notes/r-utility-drawers.js.md#§2 */
const WAIT_TIMEOUT_MS = 800;

/**
 * Resolve a `<dialog-component>` by id and make sure it has been upgraded.
 * The rail's markup can parse before dialog.js has executed, in which case the
 * element is still an unupgraded HTMLElement with no showDialog method.
 *
 * The whenDefined race is what keeps a click from being swallowed: the caller has
 * already cancelled the navigation by this point, so a module that never arrives
 * must resolve to null and let the caller fall back to the href rather than
 * leaving the shopper on a dead link.
 *
 * @param {string} id
 * @returns {Promise<HTMLElement | null>}
 */
async function resolveDrawer(id) {
  const host = document.getElementById(id);
  if (!host) return null;

  const defined = await Promise.race([
    customElements.whenDefined(host.localName).then(() => true),
    new Promise((resolve) => setTimeout(() => resolve(false), WAIT_TIMEOUT_MS)),
  ]);
  if (!defined) return null;

  customElements.upgrade(host);

  return typeof (/** @type {any} */ (host).showDialog) === 'function' ? host : null;
}

async function reopenFlaggedDrawer() {
  const marker = document.querySelector(AUTO_OPEN_SELECTOR);
  if (!marker) return;

  const host = marker.closest('dialog-component');
  if (!host?.id) return;

  const drawer = await resolveDrawer(host.id);
  /** @type {any} */ (drawer)?.showDialog();
}

/* ↗ .cursor/references/code-notes/r-utility-drawers.js.md#§3 */
async function closeMenuDrawer() {
  const drawer = document.querySelector('header-drawer');
  if (!(drawer instanceof HTMLElement)) return;

  const host = /** @type {any} */ (drawer);
  if (typeof host.close !== 'function' || host.isOpen !== true) return;

  const panel = drawer.querySelector('.menu-drawer');
  host.close();

  await new Promise((resolve) => requestAnimationFrame(resolve));
  await Promise.race([
    panel ? onAnimationEnd(panel, () => {}, { subtree: false }) : Promise.resolve(),
    new Promise((resolve) => setTimeout(resolve, WAIT_TIMEOUT_MS)),
  ]);
}

/* ↗ .cursor/references/code-notes/r-utility-drawers.js.md#§5 */
const DIALOG_SOURCE_SELECTOR = 'dialog-component, r-deferred-dialog, cart-drawer-component';

/* ↗ .cursor/references/code-notes/r-utility-drawers.js.md#§6 */
const RETURN_ATTRIBUTE = 'data-r-drawer-return';

/** @type {WeakMap<Element, AbortController>} */
const pendingReturns = new WeakMap();

/**
 * Drop a panel's pending reopen, because it is now the thing being closed.
 *
 * @param {Element | null} panel
 */
function cancelReturn(panel) {
  if (!panel) return;
  pendingReturns.get(panel)?.abort();
  pendingReturns.delete(panel);
}

/**
 * Close whatever the trigger was fired from, and resolve once it has gone.
 *
 * A dialog source is the easy case: closeDialog() is async and already awaits its
 * own exit animation and scroll unlock, so awaiting it is enough. Doing it at all
 * is not optional — showModal() on a second dialog puts both in the top layer,
 * leaving the first panel stranded behind the new backdrop with its own scroll
 * lock still applied.
 *
 * A trigger with no dialog ancestor came from the menu drawer, which is not a
 * <dialog> and needs the animation dance below.
 *
 * @param {Element} trigger
 * @returns {Promise<Element | null>} The source that was closed, if it was a dialog.
 */
async function closeSource(trigger) {
  const source = trigger.closest(DIALOG_SOURCE_SELECTOR);

  if (source) {
    cancelReturn(source);
    const host = /** @type {any} */ (source);
    if (typeof host.closeDialog === 'function') await host.closeDialog();
    return source;
  }

  await closeMenuDrawer();
  return null;
}

/**
 * Cancel the navigation and take over — but only on the synchronous evidence that
 * a panel with that id is in the document. preventDefault() has to happen inside
 * the listener itself; deferring it behind an `await` leaves the browser free to
 * navigate out from under the handler.
 *
 * @param {MouseEvent} event
 */
function onRemoteTriggerClick(event) {
  // Let modified clicks (new tab, download, …) fall through to the href.
  if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) {
    return;
  }

  const trigger = event.target instanceof Element ? event.target.closest(REMOTE_TRIGGER_SELECTOR) : null;
  if (!(trigger instanceof HTMLAnchorElement)) return;

  const id = trigger.dataset.rDrawerOpen;
  if (!id || !document.getElementById(id)) return;

  event.preventDefault();
  openRemoteDrawer(trigger, id, trigger.href);
}

/**
 * @param {Element} trigger - What was clicked; identifies the source to close.
 * @param {string} id
 * @param {string} href - Where to go if the panel turns out not to be usable.
 */
async function openRemoteDrawer(trigger, id, href) {
  const drawer = await resolveDrawer(id);

  if (!drawer) {
    location.assign(href);
    return;
  }

  // A lazy panel (r-deferred-dialog) fetches its body now, under the source's exit.
  /** @type {any} */ (drawer).ensureBody?.();

  const source = await closeSource(trigger);

  if (source && trigger.hasAttribute(RETURN_ATTRIBUTE)) {
    cancelReturn(drawer);
    const controller = new AbortController();
    pendingReturns.set(drawer, controller);
    drawer.addEventListener(
      'dialog:close',
      () => {
        pendingReturns.delete(drawer);
        if (source.isConnected) /** @type {any} */ (source).showDialog?.();
      },
      { once: true, signal: controller.signal }
    );
  }

  /** @type {any} */ (drawer).showDialog();
}

/* ↗ .cursor/references/code-notes/r-utility-drawers.js.md#§4 */
const CHAT_TRIGGER_SELECTOR = '[data-r-chat-open]';

/**
 * Resolve once the Gorgias widget is usable — and never, if the app embed is off.
 *
 * @returns {Promise<unknown>}
 */
function whenChatReady() {
  const chat = /** @type {any} */ (window).GorgiasChat;
  if (chat) return chat.init();

  return new Promise((resolve) => {
    window.addEventListener('gorgias-widget-loaded', () => resolve(undefined), { once: true });
  });
}

async function revealChatTriggers() {
  const triggers = document.querySelectorAll(CHAT_TRIGGER_SELECTOR);
  if (!triggers.length) return;

  try {
    await whenChatReady();
  } catch {
    return;
  }

  for (const trigger of triggers) {
    if (trigger instanceof HTMLElement) trigger.hidden = false;
  }
}

/**
 * @param {MouseEvent} event
 */
async function onChatTriggerClick(event) {
  const trigger = event.target instanceof Element ? event.target.closest(CHAT_TRIGGER_SELECTOR) : null;
  if (!trigger) return;

  await closeSource(trigger);
  /** @type {any} */ (window).GorgiasChat?.open();
}

document.addEventListener('click', onRemoteTriggerClick);
document.addEventListener('click', onChatTriggerClick);
// The Contact body arrives on demand (r-deferred-dialog.js); its chat button is in it.
document.addEventListener('r-drawer:body', revealChatTriggers);

reopenFlaggedDrawer();
revealChatTriggers();
